import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom'; 
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const VideoBook = () => {
  const authState = useSelector((state) => state.authReducer);
  const location = useLocation(); 
  const navigate = useNavigate();
  const [userId] = useState(authState.user._id);
  const [userName] = useState(authState.user.name);
  const [bookId] = useState(location.state.bookId); 

  const [videos, setVideos] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [currentPage, setCurrentPage] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const videosPerPage = 1;

  // Fetch Videos
  const fetchVideos = useCallback(() => {
    const config = {
      url: '/myreadingnotesurls/linksforvideobook',
      method: 'post',
      headers: { Authorization: authState.token },
      data: { bookId, userId },
    };

    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        if (response && response.status) {
          setVideos(response.urls);
        } else {
          console.error('Video URL data not found in response');
        }
      })
      .catch((error) => {
        console.error('Error fetching Video data:', error);
      });
  }, [authState.token, fetchData, bookId, userId]);

  useEffect(() => {
    if (authState.isLoggedIn) fetchVideos();
  }, [authState.isLoggedIn, fetchVideos]);

  const handlePageChange = (index) => {
    setCurrentPage(index);
  };

  const handleClose = () => {
    // navigate("/videobook-home");
    navigate(-1);
  };

  const handleLinkClick = (note) => {
    if (note.linkAddress.includes("youtube.com") || note.linkAddress.includes("youtu.be")) {
      let videoId;
      if (note.linkAddress.includes("youtube.com")) {
        videoId = new URLSearchParams(new URL(note.linkAddress).search).get('v');
      } else {
        const videoId = note?.linkAddress?.split('/').pop() || ''; 
      }
      const embedLink = `https://www.youtube.com/embed/${videoId}?autoplay=1`; 
      setVideoUrl(embedLink); 
      setShowVideo(true);     
    } else {
      window.open(note.linkAddress, "_blank");
    }
  };

  // Slider Settings
  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: handlePageChange,
    appendDots: (dots) => (
      <div style={{ position: 'absolute', bottom: '10px', width: '100%', display: 'flex', justifyContent: 'center' }}>
        <ul style={{ margin: '0px' }}>{dots}</ul>
      </div>
    ),
    customPaging: (i) => (
      <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#ddd', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '5px' }}>
        {i + 1}
      </div>
    ),
  };

  // Render Videos
  const renderVideos = () => {
    const startIndex = currentPage * videosPerPage;
    const selectedVideos = videos.slice(startIndex, startIndex + videosPerPage);

    return selectedVideos.map((note) => (
      <div key={note._id} className="p-1 text-gray-600 rounded-md shadow-md text-sm" style={{ height: '90vh', overflow: 'auto' }}>
        <div className="w-full h-full">
          <div className="text-sm text-gray-500 flex justify-between">
            <div>
              <p>VideoBook@Think5Whys</p>
              <p>{userName}</p>
            </div>
            <div className="flex flex-row justify-center items-center text-xs">
              <p className="text-xs text-red-600 flex justify-center items-center w-6/12">Page# {note.pageNumber}</p>
              <button className="text-xs max-h-8 max-w-10" onClick={handleClose}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          {/* Book Title and Link */}
          <div className="flex flex-col">
            <p className="text-m text-blue-600 bg-yellow-300">Website: {note.linkName}</p>
            <p className="text-lg text-white bg-purple-400 mb-4 h-8">Channel: {note.linkPageName}</p>
          </div>

          {/* Video Link */}
          <div className="flex flex-col box border-5 border-blue-500 py-2 px-2 h-64">
            {!showVideo ? (
              <p className="text-lg cursor-pointer text-blue-600" onClick={() => handleLinkClick(note)}>
                {note.linkAddress}
              </p>
            ) : (
              <iframe
                width="100%"
                height="100%"
                src={videoUrl}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video Player"
              ></iframe>
            )}
          </div>

          {/* Video Description */}
          <div className="flex flex-col justify-center items-center">
            <p className="text-m text-blue-900">{note.linkDetails}</p>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className="min-h-full my-2 mx-auto max-w-[600px] py-4 bg-lime-200 text-blue-800">
      {loading ? (
        <Loader />
      ) : (
        <Slider {...sliderSettings}>
          {videos.length === 0 ? (
            <div className="flex flex-col justify-center items-center p-4 text-gray-600 text-center h-96">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl mb-2 text-red-600" />
              <p className="text-lg">No records available!</p>
              <button className="text-xs max-h-8 max-w-10" onClick={handleClose}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ) : (
            Array.from({ length: Math.ceil(videos.length / videosPerPage) }, (_, index) => (
              <div key={index}>{renderVideos()}</div>
            ))
          )}
        </Slider>
      )}
    </div>
  );
};

export default VideoBook;
